import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LogOut, Users, Lock, Globe, Check, X, Edit2, Package, BarChart3, Plus, ExternalLink, Loader2, ChevronDown, Award, Phone, EyeOff } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';
import GlowBadge from '@/components/GlowBadge';
import BrandAutocomplete from '@/components/BrandAutocomplete';
import { auth, RecaptchaVerifier, signInWithPhoneNumber } from '@/lib/firebase';

export default function SettingsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dailyStatEnabled, setDailyStatEnabled] = useState(true);
  const [isPrivate, setIsPrivate] = useState(false);
  const [hidePhotos, setHidePhotos] = useState(false);
  const [loading, setLoading] = useState(true);
  const [followRequests, setFollowRequests] = useState([]);
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [showSuggestForm, setShowSuggestForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '', brand: '', category: 'skincare', product_type: 'cleanser', price: '', purchase_url: ''
  });
  const [suggestForm, setSuggestForm] = useState({
    name: '', brand: '', category: 'skincare', product_type: 'cleanser', price: '', purchase_url: ''
  });
  // Phone linking
  const [phoneLinkMode, setPhoneLinkMode] = useState('idle'); // idle, input, otp, linked
  const [linkPhone, setLinkPhone] = useState('');
  const [linkCountryCode, setLinkCountryCode] = useState('+91');
  const [linkOtp, setLinkOtp] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState('');

  const productTypes = {
    skincare: ['cleanser', 'toner', 'serum', 'moisturizer', 'sunscreen', 'exfoliator', 'face_mask', 'eye_cream', 'lip_balm', 'body_lotion', 'body_wash', 'micellar_water', 'skin_treatment'],
    makeup: ['lipstick', 'lip_gloss', 'lip_liner', 'foundation', 'concealer', 'primer', 'blush', 'highlighter', 'bronzer', 'eyeshadow', 'eyeliner', 'eyebrow_pencil', 'kajal', 'mascara', 'compact', 'setting_spray', 'nail_polish'],
    haircare: ['shampoo', 'conditioner', 'hair_oil', 'hair_serum', 'hair_mask', 'hair_color', 'hair_treatment', 'dry_shampoo', 'hair_spray'],
    pharmacy: ['supplement', 'derma_product']
  };

  const fetchUser = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/auth/me`);
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setDailyStatEnabled(userData.daily_stat_enabled);
        setIsPrivate(userData.is_private || false);
        setHidePhotos(userData.hide_photos || false);
        setNewUsername(userData.username || '');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFollowRequests = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/follow-requests`);
      if (response.ok) {
        const data = await response.json();
        setFollowRequests(data);
      }
    } catch (error) {
      console.error('Error fetching follow requests:', error);
    }
  }, []);

  const fetchMySubmissions = useCallback(async () => {
    try {
      const response = await authFetch(`${API}/products/submissions`);
      if (response.ok) setMySubmissions(await response.json());
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  }, []);

  useEffect(() => {
    fetchUser();
    fetchFollowRequests();
    fetchMySubmissions();
  }, [fetchUser, fetchFollowRequests, fetchMySubmissions]);

  const handleToggleStats = async () => {
    try {
      const response = await authFetch(`${API}/users/toggle-stats`, {
        method: 'POST',
      });

      if (response.ok) {
        const result = await response.json();
        setDailyStatEnabled(result.daily_stat_enabled);
        toast.success(`Daily stats ${result.daily_stat_enabled ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      console.error('Error toggling stats:', error);
      toast.error('Failed to update settings');
    }
  };

  const handleTogglePrivacy = async () => {
    try {
      const response = await authFetch(`${API}/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_private: !isPrivate })
      });

      if (response.ok) {
        setIsPrivate(!isPrivate);
        toast.success(`Account is now ${!isPrivate ? 'private' : 'public'}`);
      }
    } catch (error) {
      console.error('Error toggling privacy:', error);
      toast.error('Failed to update settings');
    }
  };

  const handleToggleHidePhotos = async () => {
    try {
      const response = await authFetch(`${API}/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hide_photos: !hidePhotos })
      });

      if (response.ok) {
        setHidePhotos(!hidePhotos);
        toast.success(hidePhotos ? 'Photos are now visible to everyone' : 'Photos are now hidden from non-followers');
      }
    } catch (error) {
      console.error('Error toggling hide photos:', error);
      toast.error('Failed to update settings');
    }
  };

  const handleUpdateUsername = async () => {
    if (!newUsername.trim()) {
      toast.error('Username cannot be empty');
      return;
    }

    try {
      const response = await authFetch(`${API}/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: newUsername.toLowerCase().replace(/\s+/g, '') })
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        setEditingUsername(false);
        toast.success('Username updated!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update username');
      }
    } catch (error) {
      console.error('Error updating username:', error);
      toast.error('Failed to update username');
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      const response = await authFetch(`${API}/follow-requests/${requestId}/accept`, {
        method: 'POST',
      });

      if (response.ok) {
        setFollowRequests(followRequests.filter(r => r.request_id !== requestId));
        toast.success('Follow request accepted');
      }
    } catch (error) {
      console.error('Error accepting request:', error);
      toast.error('Failed to accept request');
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      const response = await authFetch(`${API}/follow-requests/${requestId}/decline`, {
        method: 'POST',
      });

      if (response.ok) {
        setFollowRequests(followRequests.filter(r => r.request_id !== requestId));
        toast.success('Follow request declined');
      }
    } catch (error) {
      console.error('Error declining request:', error);
      toast.error('Failed to decline request');
    }
  };

  const handleSuggestProduct = async (e) => {
    e.preventDefault();
    if (!suggestForm.name || !suggestForm.brand || !suggestForm.purchase_url) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (suggestForm.product_type === 'other' && !suggestForm.custom_type?.trim()) {
      toast.error('Please describe the product type');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...suggestForm };
      if (payload.product_type === 'other' && payload.custom_type) {
        payload.product_type = payload.custom_type.toLowerCase().replace(/\s+/g, '_');
      }
      delete payload.custom_type;

      const response = await authFetch(`${API}/products/suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setSuggestForm({ name: '', brand: '', category: 'skincare', product_type: 'cleanser', price: '', purchase_url: '', custom_type: '' });
        setShowSuggestForm(false);
        fetchMySubmissions();
      } else {
        toast.error(data.detail || 'Failed to submit product');
      }
    } catch (error) {
      toast.error('Failed to submit product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmission = async (submissionId) => {
    if (!editForm.name || !editForm.brand || !editForm.purchase_url) {
      toast.error('Please fill in all required fields');
      return;
    }
    setEditSubmitting(true);
    try {
      const response = await authFetch(`${API}/products/submissions/${submissionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Suggestion updated');
        setEditingSubmission(null);
        fetchMySubmissions();
      } else {
        toast.error(data.detail || 'Failed to update suggestion');
      }
    } catch (error) {
      toast.error('Failed to update suggestion');
    } finally {
      setEditSubmitting(false);
    }
  };

  // Initialize reCAPTCHA for phone linking - visible checkbox
  const [linkRecaptchaSolved, setLinkRecaptchaSolved] = useState(false);
  const linkRecaptchaInit = useRef(false);

  useEffect(() => {
    if (phoneLinkMode !== 'input') {
      linkRecaptchaInit.current = false;
      setLinkRecaptchaSolved(false);
      return;
    }

    const timer = setTimeout(() => {
      if (linkRecaptchaInit.current) return;
      linkRecaptchaInit.current = true;

      if (window.__gloverseLinkRecaptcha) {
        try { window.__gloverseLinkRecaptcha.clear(); } catch (e) { console.error('Link reCAPTCHA clear error:', e); }
        window.__gloverseLinkRecaptcha = null;
      }
      const container = document.getElementById('link-recaptcha');
      if (container) { while (container.firstChild) container.removeChild(container.firstChild); }

      try {
        const verifier = new RecaptchaVerifier(auth, 'link-recaptcha', {
          size: 'normal',
          callback: () => { setLinkRecaptchaSolved(true); setLinkError(''); },
          'expired-callback': () => { setLinkRecaptchaSolved(false); }
        });
        window.__gloverseLinkRecaptcha = verifier;
        verifier.render().catch(err => console.error('Link reCAPTCHA render error:', err));
      } catch (e) {
        console.error('Link reCAPTCHA init error:', e);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [phoneLinkMode]);

  // Cleanup reCAPTCHA on unmount
  useEffect(() => {
    return () => {
      if (window.__gloverseLinkRecaptcha) {
        try { window.__gloverseLinkRecaptcha.clear(); } catch (e) { console.error('Link reCAPTCHA clear error:', e); }
        window.__gloverseLinkRecaptcha = null;
      }
    };
  }, []);

  const handleSendLinkOTP = async () => {
    const fullNumber = `${linkCountryCode}${linkPhone.replace(/\D/g, '')}`;
    if (linkPhone.replace(/\D/g, '').length < 8) {
      setLinkError('Please enter a valid phone number');
      return;
    }
    if (!linkRecaptchaSolved || !window.__gloverseLinkRecaptcha) {
      setLinkError('Please complete the verification checkbox first.');
      return;
    }
    setLinkLoading(true);
    setLinkError('');
    try {
      const confirmationResult = await signInWithPhoneNumber(auth, fullNumber, window.__gloverseLinkRecaptcha);
      window.__gloverseLinkConfirmation = confirmationResult;
      setPhoneLinkMode('otp');
    } catch (e) {
      console.error('Link OTP error:', e.code, e.message);
      setLinkError(`Failed to send OTP: ${e.message || e.code || 'Unknown error'}`);
      setLinkRecaptchaSolved(false);
      linkRecaptchaInit.current = false;
    } finally {
      setLinkLoading(false);
    }
  };

  const handleVerifyLinkOTP = async () => {
    if (linkOtp.length !== 6) { setLinkError('Enter 6-digit OTP'); return; }
    setLinkLoading(true);
    setLinkError('');
    try {
      const confirmationResult = window.__gloverseLinkConfirmation;
      if (!confirmationResult) {
        setLinkError('Session expired. Please go back and resend OTP.');
        setLinkLoading(false);
        return;
      }
      const result = await confirmationResult.confirm(linkOtp);
      const idToken = await result.user.getIdToken();
      const response = await authFetch(`${API}/auth/phone/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_token: idToken })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Linking failed');
      }
      toast.success('Phone number linked!');
      setPhoneLinkMode('linked');
      setUser(prev => ({ ...prev, phone: `${linkCountryCode}${linkPhone}` }));
    } catch (e) {
      setLinkError(e.message || 'Verification failed');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      localStorage.removeItem('gloverse_token');
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-2 hover:bg-primary/10 rounded-full transition-colors">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold ml-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Settings</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* User Info */}
        <div className="bg-white rounded-2xl p-6 border border-border">
          <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Account</h2>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Name</p>
              <p className="font-medium">{user?.name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email (private)</p>
              {user?.email ? (
                <div className="flex items-center gap-2">
                  <p className="font-medium">{user.email}</p>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Linked</span>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">No email linked yet. Connect your Google account to add email.</p>
                  <button
                    data-testid="link-google-btn"
                    onClick={() => {
                      const redirectUrl = window.location.origin + '/settings?link=google';
                      const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
                      if (GOOGLE_CLIENT_ID) {
                        const state = crypto.randomUUID();
                        localStorage.setItem('google_oauth_state', state);
                        localStorage.setItem('google_link_mode', 'true');
                        const params = new URLSearchParams({
                          client_id: GOOGLE_CLIENT_ID,
                          redirect_uri: `${window.location.origin}/auth/google/callback`,
                          response_type: 'code',
                          scope: 'openid email profile',
                          state: state,
                          access_type: 'offline',
                          prompt: 'select_account'
                        });
                        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-sm bg-white border-2 border-border rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Link Google Account
                  </button>
                </div>
              )}
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Username (public)</p>
              {editingUsername ? (
                <div className="flex gap-2">
                  <input
                    data-testid="username-input"
                    type="text"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    placeholder="Enter username"
                    className="flex-1 px-3 py-2 rounded-xl border border-border focus:border-primary outline-none"
                  />
                  <button
                    data-testid="save-username-btn"
                    onClick={handleUpdateUsername}
                    className="p-2 bg-primary text-white rounded-xl hover:bg-primary/90"
                  >
                    <Check className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => {
                      setEditingUsername(false);
                      setNewUsername(user?.username || '');
                    }}
                    className="p-2 bg-gray-100 rounded-xl hover:bg-gray-200"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="font-medium">@{user?.username || user?.name?.replace(/\s+/g, '').toLowerCase()}</p>
                  <button
                    data-testid="edit-username-btn"
                    onClick={() => setEditingUsername(true)}
                    className="p-1 hover:bg-gray-100 rounded-full"
                  >
                    <Edit2 className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Link Phone Number */}
        <div className="bg-white rounded-2xl p-6 border border-border" data-testid="link-phone-section">
          <div className="flex items-center gap-2 mb-4">
            <Phone className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Phone Number</h2>
          </div>

          {user?.phone ? (
            <div className="flex items-center gap-2">
              <p className="font-medium text-foreground">{user.phone}</p>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Linked</span>
            </div>
          ) : phoneLinkMode === 'idle' ? (
            <div>
              <p className="text-sm text-muted-foreground mb-3">Link your phone number so you can log in with OTP as well.</p>
              <button
                data-testid="start-link-phone-btn"
                onClick={() => setPhoneLinkMode('input')}
                className="btn-primary px-4 py-2 text-sm"
              >
                Link Phone Number
              </button>
            </div>
          ) : phoneLinkMode === 'input' ? (
            <div className="space-y-3">
              <div className="flex gap-2">
                <select value={linkCountryCode} onChange={(e) => setLinkCountryCode(e.target.value)} className="w-20 px-2 py-2 rounded-xl border border-border text-sm">
                  <option value="+91">+91</option>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+61">+61</option>
                  <option value="+971">+971</option>
                  <option value="+65">+65</option>
                </select>
                <input
                  data-testid="link-phone-input"
                  type="tel"
                  value={linkPhone}
                  onChange={(e) => setLinkPhone(e.target.value)}
                  placeholder="Phone number"
                  className="flex-1 px-3 py-2 rounded-xl border border-border focus:border-primary outline-none text-sm"
                />
              </div>
              {/* Visible reCAPTCHA for Safari compatibility */}
              <div className="flex justify-center" id="link-recaptcha" data-testid="link-recaptcha-container" />
              {linkError && <p className="text-sm text-red-500">{linkError}</p>}
              <div className="flex gap-2">
                <button onClick={handleSendLinkOTP} disabled={linkLoading || !linkRecaptchaSolved} className="btn-primary px-4 py-2 text-sm disabled:opacity-50" data-testid="send-link-otp-btn">
                  {linkLoading ? 'Sending...' : 'Send OTP'}
                </button>
                <button onClick={() => { setPhoneLinkMode('idle'); setLinkError(''); }} className="px-4 py-2 text-sm bg-gray-100 rounded-xl">Cancel</button>
              </div>
            </div>
          ) : phoneLinkMode === 'otp' ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Enter the 6-digit code sent to {linkCountryCode} {linkPhone}</p>
              <input
                data-testid="link-otp-input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={linkOtp}
                onChange={(e) => setLinkOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="w-full px-3 py-2 rounded-xl border border-border focus:border-primary outline-none text-center text-lg tracking-[0.5em] font-mono"
              />
              {linkError && <p className="text-sm text-red-500">{linkError}</p>}
              <div className="flex gap-2">
                <button onClick={handleVerifyLinkOTP} disabled={linkLoading} className="btn-primary px-4 py-2 text-sm disabled:opacity-50" data-testid="verify-link-otp-btn">
                  {linkLoading ? 'Verifying...' : 'Verify & Link'}
                </button>
                <button onClick={() => { setPhoneLinkMode('input'); setLinkOtp(''); setLinkError(''); }} className="px-4 py-2 text-sm bg-gray-100 rounded-xl">Back</button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <p className="font-medium text-foreground">{linkCountryCode}{linkPhone}</p>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Linked</span>
            </div>
          )}
        </div>

        {/* Follow Requests */}
        {followRequests.length > 0 && (
          <div className="bg-white rounded-2xl p-6 border border-border">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Follow Requests ({followRequests.length})
              </h2>
            </div>
            <div className="space-y-3">
              {followRequests.map(request => (
                <div key={request.request_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  {request.from_user_picture ? (
                    <img src={request.from_user_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold">
                      {request.from_username?.charAt(0).toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-medium">@{request.from_username}</p>
                  </div>
                  <button
                    data-testid={`accept-request-${request.request_id}`}
                    onClick={() => handleAcceptRequest(request.request_id)}
                    className="p-2 bg-primary text-white rounded-full hover:bg-primary/90"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`decline-request-${request.request_id}`}
                    onClick={() => handleDeclineRequest(request.request_id)}
                    className="p-2 bg-gray-200 rounded-full hover:bg-gray-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Privacy */}
        <div className="bg-white rounded-2xl p-6 border border-border">
          <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Privacy</h2>
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isPrivate ? <Lock className="w-5 h-5 text-muted-foreground" /> : <Globe className="w-5 h-5 text-muted-foreground" />}
                <div>
                  <p className="font-medium">{isPrivate ? 'Private Account' : 'Public Account'}</p>
                  <p className="text-sm text-muted-foreground">
                    {isPrivate ? 'Only approved followers can see your posts' : 'Anyone can see your posts'}
                  </p>
                </div>
              </div>
              <button
                data-testid="toggle-privacy-btn"
                onClick={handleTogglePrivacy}
                className={`relative w-14 h-8 rounded-full transition-colors ${
                  isPrivate ? 'bg-primary' : 'bg-border'
                }`}
              >
                <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                  isPrivate ? 'translate-x-6' : 'translate-x-0'
                }`} />
              </button>
            </div>

            {/* Hide Photos toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <EyeOff className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Hide Photos from Public</p>
                  <p className="text-sm text-muted-foreground">
                    {hidePhotos ? 'Posts & Looks hidden from non-followers. Checklists stay visible.' : 'Everyone can see your posts and looks'}
                  </p>
                </div>
              </div>
              <button
                data-testid="toggle-hide-photos-btn"
                onClick={handleToggleHidePhotos}
                className={`relative w-14 h-8 rounded-full transition-colors shrink-0 ${
                  hidePhotos ? 'bg-primary' : 'bg-border'
                }`}
              >
                <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                  hidePhotos ? 'translate-x-6' : 'translate-x-0'
                }`} />
              </button>
            </div>
          </div>
        </div>

        {/* Preferences */}
        <div className="bg-white rounded-2xl p-6 border border-border">
          <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Preferences</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Daily Stats</p>
              <p className="text-sm text-muted-foreground">Show daily community insights</p>
            </div>
            <button
              data-testid="toggle-stats-btn"
              onClick={handleToggleStats}
              className={`relative w-14 h-8 rounded-full transition-colors ${
                dailyStatEnabled ? 'bg-primary' : 'bg-border'
              }`}
            >
              <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                dailyStatEnabled ? 'translate-x-6' : 'translate-x-0'
              }`} />
            </button>
          </div>
        </div>

        {/* Glow Badge */}
        <div className="bg-white rounded-2xl p-6 border border-border">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Glow Badge</h2>
          </div>
          {user?.glow_badge ? (
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted-foreground">Your badge:</p>
              <GlowBadge tier={user.glow_badge} size="md" />
              <span className="text-sm font-medium">{user.glow_badge === 'glow_pro' ? 'Glow Pro' : 'Glow'}</span>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-3">Get verified as a beauty creator on Gloverse</p>
              <button
                data-testid="request-badge-btn"
                onClick={() => navigate('/badge-request')}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-colors font-medium"
              >
                <GlowBadge tier="glow" />
                Apply for Glow Badge
              </button>
            </>
          )}
        </div>

        {/* Suggest a Product */}
        <div className="bg-white rounded-2xl p-6 border border-border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Suggest a Product</h2>
            </div>
            <button
              data-testid="toggle-suggest-form"
              onClick={() => setShowSuggestForm(!showSuggestForm)}
              className="text-sm text-primary font-medium hover:underline"
            >
              {showSuggestForm ? 'Cancel' : 'Add Product'}
            </button>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Can't find a product? Suggest it and we'll add it to the database.
          </p>

          {showSuggestForm && (
            <form onSubmit={handleSuggestProduct} className="space-y-3">
              <input
                data-testid="suggest-name"
                type="text"
                value={suggestForm.name}
                onChange={(e) => setSuggestForm({...suggestForm, name: e.target.value})}
                placeholder="Product name *"
                className="w-full px-4 py-2.5 rounded-xl border border-border bg-white/50 focus:border-primary outline-none text-sm placeholder:text-muted-foreground/40"
                required
              />
              <BrandAutocomplete
                value={suggestForm.brand}
                onChange={(brand) => setSuggestForm({...suggestForm, brand})}
                category={suggestForm.category}
              />
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <select
                    data-testid="suggest-category"
                    value={suggestForm.category}
                    onChange={(e) => setSuggestForm({...suggestForm, category: e.target.value, product_type: productTypes[e.target.value][0]})}
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-white/50 focus:border-primary outline-none text-sm appearance-none"
                  >
                    <option value="skincare">Skincare</option>
                    <option value="makeup">Makeup</option>
                    <option value="haircare">Haircare</option>
                    <option value="pharmacy">Pharmacy</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                </div>
                <div className="relative">
                  <select
                    data-testid="suggest-type"
                    value={suggestForm.product_type}
                    onChange={(e) => setSuggestForm({...suggestForm, product_type: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-white/50 focus:border-primary outline-none text-sm appearance-none"
                  >
                    {(productTypes[suggestForm.category] || []).map(t => (
                      <option key={t} value={t}>{t.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                </div>
              </div>
              {suggestForm.product_type === 'other' && (
                <input
                  data-testid="suggest-custom-type"
                  type="text"
                  value={suggestForm.custom_type || ''}
                  onChange={(e) => setSuggestForm({...suggestForm, custom_type: e.target.value})}
                  placeholder="Describe the product type *"
                  className="w-full px-4 py-2.5 rounded-xl border border-primary bg-primary/5 focus:border-primary outline-none text-sm placeholder:text-muted-foreground/40"
                />
              )}
              <input
                data-testid="suggest-price"
                type="text"
                value={suggestForm.price}
                onChange={(e) => setSuggestForm({...suggestForm, price: e.target.value})}
                placeholder="Price (e.g. ₹499)"
                className="w-full px-4 py-2.5 rounded-xl border border-border bg-white/50 focus:border-primary outline-none text-sm placeholder:text-muted-foreground/40"
              />
              <input
                data-testid="suggest-url"
                type="url"
                value={suggestForm.purchase_url}
                onChange={(e) => setSuggestForm({...suggestForm, purchase_url: e.target.value})}
                placeholder="Purchase URL (Amazon, Flipkart, Nykaa, etc.) *"
                className="w-full px-4 py-2.5 rounded-xl border border-border bg-white/50 focus:border-primary outline-none text-sm placeholder:text-muted-foreground/40"
                required
              />
              <button
                data-testid="submit-suggest"
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium disabled:opacity-50"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {submitting ? 'Verifying & Submitting...' : 'Submit Product'}
              </button>
            </form>
          )}

          {mySubmissions.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Your Suggestions ({mySubmissions.length})</p>
              {mySubmissions.map(sub => (
                <div key={sub.submission_id} className="p-3 bg-gray-50 rounded-lg text-sm space-y-2" data-testid={`submission-${sub.submission_id}`}>
                  {editingSubmission === sub.submission_id ? (
                    <div className="space-y-2">
                      <input
                        data-testid="edit-submission-name"
                        value={editForm.name}
                        onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                        placeholder="Product name"
                        className="w-full px-3 py-2 rounded-lg border border-border text-sm"
                      />
                      <BrandAutocomplete
                        value={editForm.brand}
                        onChange={(brand) => setEditForm({...editForm, brand})}
                        category={editForm.category}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={editForm.category}
                          onChange={(e) => setEditForm({...editForm, category: e.target.value, product_type: productTypes[e.target.value]?.[0] || ''})}
                          className="px-3 py-2 rounded-lg border border-border text-sm"
                        >
                          <option value="skincare">Face Care</option>
                          <option value="makeup">Make Up</option>
                          <option value="haircare">Hair Care</option>
                          <option value="pharmacy">Pharma</option>
                        </select>
                        <select
                          value={editForm.product_type}
                          onChange={(e) => setEditForm({...editForm, product_type: e.target.value})}
                          className="px-3 py-2 rounded-lg border border-border text-sm"
                        >
                          {(productTypes[editForm.category] || []).map(t => (
                            <option key={t} value={t}>{t.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</option>
                          ))}
                        </select>
                      </div>
                      <input
                        value={editForm.price}
                        onChange={(e) => setEditForm({...editForm, price: e.target.value})}
                        placeholder="Price (e.g. ₹499)"
                        className="w-full px-3 py-2 rounded-lg border border-border text-sm"
                      />
                      <input
                        value={editForm.purchase_url}
                        onChange={(e) => setEditForm({...editForm, purchase_url: e.target.value})}
                        placeholder="Purchase URL"
                        className="w-full px-3 py-2 rounded-lg border border-border text-sm"
                      />
                      <div className="flex gap-2">
                        <button
                          data-testid="save-edit-submission"
                          onClick={() => handleEditSubmission(sub.submission_id)}
                          disabled={editSubmitting}
                          className="flex-1 px-3 py-2 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90 disabled:opacity-50"
                        >
                          {editSubmitting ? 'Saving...' : 'Save & Resubmit'}
                        </button>
                        <button
                          onClick={() => setEditingSubmission(null)}
                          className="px-3 py-2 bg-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                      {(sub.status === 'approved' || sub.status === 'auto_approved') && (
                        <p className="text-xs text-amber-600">Editing will re-submit for approval. The current product will be removed until re-approved.</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{sub.name}</p>
                          <p className="text-xs text-muted-foreground">{sub.brand} · {sub.product_type?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} · {sub.price || 'N/A'}</p>
                        </div>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${
                          sub.status === 'auto_approved' || sub.status === 'approved'
                            ? 'bg-green-50 text-green-600'
                            : sub.status === 'pending'
                            ? 'bg-amber-50 text-amber-600'
                            : 'bg-red-50 text-red-600'
                        }`}>
                          {sub.status === 'auto_approved' ? 'Approved' : sub.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : ''}</span>
                        <button
                          data-testid={`edit-submission-btn-${sub.submission_id}`}
                          onClick={() => {
                            setEditingSubmission(sub.submission_id);
                            setEditForm({
                              name: sub.name || '',
                              brand: sub.brand || '',
                              category: sub.category || 'skincare',
                              product_type: sub.product_type || 'cleanser',
                              price: sub.price || '',
                              purchase_url: sub.purchase_url || ''
                            });
                          }}
                          className="text-primary hover:underline font-medium flex items-center gap-1"
                        >
                          <Edit2 className="w-3 h-3" /> Edit
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="bg-white rounded-2xl p-6 border border-border">
          {(user?.is_admin || user?.email === 'aswathsridhar14@gmail.com') && (
            <>
              <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin Tools</h2>
              <div className="space-y-3 mb-4">
                <button
                  data-testid="admin-btn"
                  onClick={() => navigate('/admin')}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary/10 text-primary rounded-xl hover:bg-primary/20 transition-colors font-medium"
                >
                  <Package className="w-5 h-5" />
                  Product Admin
                </button>
                <button
                  data-testid="dashboard-btn"
                  onClick={() => navigate('/dashboard')}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-colors font-medium"
                >
                  <BarChart3 className="w-5 h-5" />
                  Data Dashboard
                </button>
              </div>
            </>
          )}
          <div className="space-y-3">
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-destructive/10 text-destructive rounded-xl hover:bg-destructive/20 transition-colors font-medium"
            >
              <LogOut className="w-5 h-5" />
              Log Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
